import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiMoreVertical, FiRepeat } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getSalesContractListApi } from "../../api/finance.salesContract.api";
import { getProjectListApi } from "../../api/finance.project.api";
import { formatMoney, lockingStatusUi, LOCKING_STATUS_OPTIONS } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const Row = ({ label, value, color }) => (
  <Flex gap="12px" align="flex-start" mb="8px">
    <Text fontSize="14px" color={MUTED} flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={color || TEXT} flex="1" textAlign="right" noOfLines={3}>{value}</Text>
  </Flex>
);

const ContractCard = ({ item, onDetail, onTransfer }) => {
  const ui = lockingStatusUi(item?.status);
  const code = item?.locking?.apartment?.code || item?.ttkq?.locking?.apartment?.code || "";
  const title = `${item?.project?.name || ""}${code ? " - " + code : ""}`.trim() || "-";
  return (
    <Box w="100%" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
      <Flex align="flex-start" gap="8px">
        <Box as="button" type="button" onClick={onDetail} textAlign="left" flex="1">
          <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={2}>{title}</Text>
        </Box>
        <Menu placement="bottom-end" isLazy>
          <MenuButton as="button" onClick={(e) => e.stopPropagation()} aria-label="Thao tác">
            <Box color={MUTED}><FiMoreVertical size={18} /></Box>
          </MenuButton>
          <MenuList minW="180px" borderRadius="12px">
            <MenuItem icon={<FiRepeat color="#2168DA" />} onClick={onTransfer}>Chuyển HDMB</MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      <Box as="button" type="button" onClick={onDetail} textAlign="left" w="100%">
        <Flex align="center" gap="8px" mt="8px" flexWrap="wrap">
          <Text fontSize="14px" color={TEXT}>{fmtD(item?.time)}</Text>
          <Box bg={ui.bg} borderRadius="6px" px="8px" py="3px">
            <Text fontSize="11px" fontWeight={600} color={ui.color}>{ui.label}</Text>
          </Box>
        </Flex>
        <Box h="1px" bg={BORDER} my="12px" />
        <Row label="Số tiền đóng HĐMB" value={formatMoney(item?.price || 0)} color={APP.primary200} />
        <Row label="Ngày ký HĐMB" value={fmtD(item?.time)} />
        <Row label="Ngày đóng HĐMB" value={fmtD(item?.priceTime)} />
        <Row label="Khách hàng" value={item?.customer?.name || "-"} />
      </Box>
    </Box>
  );
};

const FieldBox = ({ label, value, onClick, children }) => (
  <Box flex="1" minW="48%">
    <Text fontSize="13px" color={TEXT} mb="6px">{label}</Text>
    {children || (
      <Flex as="button" type="button" onClick={onClick} align="center" justify="space-between" bg="#F7F9FC" borderRadius="8px" h="40px" px="10px" w="100%">
        <Text fontSize="14px" color={TEXT} noOfLines={1}>{value}</Text>
        <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
      </Flex>
    )}
  </Box>
);

const DateField = ({ value, onChange }) => (
  <Box position="relative" w="100%">
    <Flex align="center" justify="space-between" bg="#F7F9FC" borderRadius="8px" h="40px" px="10px">
      <Text fontSize="14px" color={value ? TEXT : MUTED}>{value ? moment(value).format("DD/MM/YYYY") : "dd/mm/yyyy"}</Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
    </Flex>
    <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} position="absolute" inset="0" w="100%" h="100%" opacity={0} p="0" cursor="pointer" />
  </Box>
);

const SalesContractList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [project, setProject] = useState({ _id: "all", name: "Tất cả" });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const sheet = useDisclosure();
  const [sheetType, setSheetType] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projSearch, setProjSearch] = useState("");
  const reqId = useRef(0);
  const projTimer = useRef(null);

  const sortOption = SORT_OPTIONS.find((x) => x.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      startTime: fromDate || undefined,
      endTime: toDate || undefined,
      status: status !== "ALL" ? status : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      projectId: project?._id && project._id !== "all" ? project._id : undefined,
    }),
    [fromDate, toDate, status, sortOption, project]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getSalesContractListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setTotal(data?.pagination?.total ?? list.length);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, project._id, fromDate, toDate, sortKey]);

  const loadProjects = useCallback((kw) => {
    getProjectListApi({ page: 1, limit: 30, search: kw || undefined })
      .then((d) => setProjects(d?.items || d || []))
      .catch(() => setProjects([]));
  }, []);

  const openSheet = (type) => {
    setSheetType(type);
    if (type === "PROJECT") { setProjSearch(""); loadProjects(""); }
    sheet.onOpen();
  };
  const onProjSearch = (e) => {
    const v = e.target.value;
    setProjSearch(v);
    if (projTimer.current) clearTimeout(projTimer.current);
    projTimer.current = setTimeout(() => loadProjects(v.trim()), 300);
  };

  const projectItems = useMemo(() => [{ _id: "all", name: "Tất cả" }, ...projects], [projects]);
  const statusLabel = LOCKING_STATUS_OPTIONS.find((s) => s.value === status)?.label || "Tất cả";

  const sheetTitle = sheetType === "STATUS" ? "Trạng thái" : sheetType === "PROJECT" ? "Dự án" : "Sắp xếp";
  const sheetOptions =
    sheetType === "STATUS" ? LOCKING_STATUS_OPTIONS.map((s) => ({ key: s.value, label: s.label, raw: s.value }))
    : sheetType === "PROJECT" ? projectItems.map((p) => ({ key: p._id, label: p.name, raw: p }))
    : SORT_OPTIONS.map((s) => ({ key: s.key, label: s.label, raw: s.key }));
  const isActive = (raw) => {
    if (sheetType === "STATUS") return raw === status;
    if (sheetType === "PROJECT") return raw?._id === project?._id;
    return raw === sortKey;
  };
  const pickItem = (raw) => {
    if (sheetType === "STATUS") setStatus(raw);
    else if (sheetType === "PROJECT") setProject(raw);
    else setSortKey(raw);
    sheet.onClose();
  };

  const extras = (
    <>
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetTitle}</Text>
            {sheetType === "PROJECT" && (
              <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
                <AppIcon src={appImages.icon_search} size={18} color="#94A3B8" />
                <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm dự án..." value={projSearch} onChange={onProjSearch} />
              </Flex>
            )}
            <Box maxH="50vh" overflowY="auto">
              {sheetOptions.map((it, i) => {
                const active = isActive(it.raw);
                const last = i === sheetOptions.length - 1;
                return (
                  <Flex key={String(it.key)} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={last ? "none" : "1px solid #EEF1F6"} onClick={() => pickItem(it.raw)}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{it.label}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  // Cùng filter/query/hành động, chỉ hiển thị dạng bảng như trang admin.
  if (isDesktop) {
    const rows = items.map((it) => ({
      id: it._id,
      raw: it,
      ui: lockingStatusUi(it?.status),
        title: `${it?.project?.name || ""}${(it?.locking?.apartment?.code || it?.ttkq?.locking?.apartment?.code) ? " - " + (it?.locking?.apartment?.code || it?.ttkq?.locking?.apartment?.code) : ""}`.trim() || "-",
        price: formatMoney(it?.price || 0),
        time: fmtD(it?.time),
        priceTime: fmtD(it?.priceTime),
        customer: it?.customer?.name || "-",
    }));

    return (
      <DesktopPage
        title="Hợp đồng mua bán"
        
      >
        <DesktopStatRow items={[{ key: "total", title: "Tổng số HĐMB", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Trạng thái
              </Text>
              <Box as="button" w="100%" h="40px" px="12px" textAlign="left" bg="#F7F9FC" borderRadius="8px" fontSize="14px" onClick={() => openSheet("STATUS")}>
                {statusLabel}
              </Box>
            </Box>
            <Box w="220px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Dự án
              </Text>
              <Box as="button" w="100%" h="40px" px="12px" textAlign="left" bg="#F7F9FC" borderRadius="8px" fontSize="14px" onClick={() => openSheet("PROJECT")}>
                {project?.name || "Tất cả"}
              </Box>
            </Box>
            <Box w="180px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Từ ngày
              </Text>
              <DateField value={fromDate} onChange={setFromDate} />
            </Box>
            <Box w="180px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Đến ngày
              </Text>
              <DateField value={toDate} onChange={setToDate} />
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có HĐMB"
            onRowClick={(row) => navigate(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/${row.id}`)}
            rows={rows}
            headers={[
              { key: "title", label: "Dự án / Căn hộ", align: "left" },
              { key: "price", label: "Số tiền đóng HĐMB", align: "right", render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={APP.primary200}>
                    {row.price}
                  </Text>
                ) },
              { key: "time", label: "Ngày ký HĐMB", align: "center" },
              { key: "priceTime", label: "Ngày đóng HĐMB", align: "center" },
              { key: "customer", label: "Khách hàng", align: "left" },
              {
                key: "status",
                label: "Trạng thái",
                render: (row) => (
                  <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="10px" py="3px">
                    <Text fontSize="12px" fontWeight={600} color={row.ui.color}>
                      {row.ui.label}
                    </Text>
                  </Box>
                ),
              },

            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {extras}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="90px">
      <AppBar title="Hợp đồng mua bán" onBack={() => navigate(EMP_BASE_PATH)} />

      <Box px="16px" pt="12px">
        <Flex as="button" w="100%" h="44px" px="12px" align="center" justify="space-between" bg="white" border="1px solid" borderColor={BORDER} borderTopRadius="12px" borderBottomRadius={filterOpen ? "0" : "12px"} borderBottom={filterOpen ? "0" : "1px solid"} borderBottomColor={BORDER} onClick={() => setFilterOpen((p) => !p)}>
          <Flex align="center" gap="8px">
            <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
          </Flex>
          <img src={filterOpen ? appImages.icon_chevron_up : appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
            <Flex flexWrap="wrap" gap="10px">
              <FieldBox label="Trạng thái" value={statusLabel} onClick={() => openSheet("STATUS")} />
              <FieldBox label="Dự án" value={project?.name || "Tất cả"} onClick={() => openSheet("PROJECT")} />
              <FieldBox label="Từ ngày"><DateField value={fromDate} onChange={setFromDate} /></FieldBox>
              <FieldBox label="Đến ngày"><DateField value={toDate} onChange={setToDate} /></FieldBox>
            </Flex>
          </Box>
        </Collapse>

        <Box mt="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
          <Text fontSize="13px" color={MUTED} fontWeight={500}>TỔNG SỐ HĐMB</Text>
          <Text fontSize="22px" fontWeight={800} color={APP.primary200} mt="8px">{total || 0}</Text>
        </Box>

        <Flex mt="16px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách HĐMB</Text>
          <Flex as="button" align="center" gap="6px" onClick={() => openSheet("SORT")}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{sortOption.label}</Text>
            <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có hợp đồng</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <ContractCard
                key={it._id}
                item={it}
                onDetail={() => navigate(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/${it._id}`)}
                onTransfer={() => navigate(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/chuyen/${it._id}`)}
              />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {extras}
    </Box>
  );
};

export default SalesContractList;
